import { DateTime } from 'luxon';

import { DASH, fromServerUtcDate, toServerDateTime } from '@/globals';
import i18n from '@/vueI18n';

import LineItem, { ILineItemAPI } from './LineItem';
import LineItemStatus from './LineItemStatus.enum';

export interface ILineItemMediaApprovalAPI {
  lineitem_mediaowner_approval_id: number;
  lineitem_id: number;
  lineitem?: ILineItemAPI;
  approval_at?: string;
  approval_comment?: string;
  approved_rejected_by: string;
  owner_id: number;
  state: string;
  submitted_at: string;
}

export default class LineItemMediaApproval {
  id: number;

  lineItemID: number;

  lineItem: LineItem;

  approvalAt?: DateTime;

  approvalComment?: string;

  ownerID: number;

  state: LineItemStatus;

  submittedAt: DateTime;

  approvedRejectedBy?: string;

  constructor(
    id = -1,
    lineItemID: number,
    lineItem: LineItem,
    approvalAt?: DateTime,
    approvalComment?: string,
    ownerID = -1,
    state = LineItemStatus.SUBMITTED,
    submittedAt?: DateTime,
    approvedRejectedBy?: string,
  ) {
    this.id = id;
    this.lineItemID = lineItemID;
    this.lineItem = lineItem;
    this.approvalAt = approvalAt;
    this.approvalComment = approvalComment;
    this.ownerID = ownerID;
    this.state = state;
    this.submittedAt = submittedAt;
    this.approvedRejectedBy = approvedRejectedBy;
  }

  get organizationName(): string {
    return this.lineItem.organization.name;
  }

  get accountName(): string {
    return this.lineItem.accountName !== null ? this.lineItem.accountName : DASH;
  }

  get origin(): string {
    return this.lineItem.origin;
  }

  get start(): string {
    return this.lineItem.startDateFormatted;
  }

  get end(): string {
    return this.lineItem.endDateFormatted;
  }

  get budget(): number {
    return this.lineItem.budget;
  }

  get purchaseTypeName(): string {
    return this.lineItem.purchaseTypeName;
  }

  get budgetFormatted(): string {
    return this.lineItem.cpmCalculator.budgetFormatted;
  }

  get status(): string {
    switch (this.state) {
      case LineItemStatus.PENDING_APPROVAL:
        return i18n.global.t('states.pendingApproval');
      case LineItemStatus.APPROVED:
        return i18n.global.t('states.approved');
      case LineItemStatus.REJECTED:
        return i18n.global.t('states.rejected');
      case LineItemStatus.CANCELLED:
        return i18n.global.t('states.cancelled');
      case LineItemStatus.DRAFT:
        return i18n.global.t('states.draft');
      case LineItemStatus.SUBMITTED:
        return i18n.global.t('states.submitted');
      default:
        return this.state;
    }
  }

  get submittedOnFormatted(): string {
    return this.submittedAt !== null ? this.submittedAt.toLocal().toLocaleString(DateTime.DATETIME_MED) : DASH;
  }

  get approvalAtFormatted(): string {
    return this.approvalAt !== null ? this.approvalAt.toLocal().toLocaleString(DateTime.DATETIME_MED) : DASH;
  }

  static fromApi(apiObject: ILineItemMediaApprovalAPI): LineItemMediaApproval {
    return new LineItemMediaApproval(
      apiObject.lineitem_mediaowner_approval_id,
      apiObject.lineitem_id,
      apiObject.lineitem != null ? LineItem.fromApi(apiObject.lineitem) : null,
      fromServerUtcDate(apiObject.approval_at),
      apiObject.approval_comment,
      apiObject.owner_id,
      apiObject.state as LineItemStatus,
      fromServerUtcDate(apiObject.submitted_at),
      apiObject.approved_rejected_by,
    );
  }

  toApi(): ILineItemMediaApprovalAPI {
    return {
      lineitem_mediaowner_approval_id: this.id >= 0 ? this.id : undefined,
      lineitem_id: this.lineItemID,
      approval_at: toServerDateTime(this.approvalAt),
      approval_comment: this.approvalComment,
      approved_rejected_by: this.approvedRejectedBy,
      owner_id: this.ownerID,
      state: this.state,
      submitted_at: toServerDateTime(this.submittedAt),
    };
  }
}
